import { routes, SOCIALS } from '@constants';
import {
  useGetDownloadedAvatar,
  useNavigateWithOfflineMode,
  useScreenSize,
} from '@hooks';
import { userApi } from '@store';
import { Button, Flex, Image, theme, Typography } from '@styles';
import { ReactComponent as FacebookIcon } from '@svgs/facebook-green.svg';
import { ReactComponent as InstagramIcon } from '@svgs/instagram-green.svg';
import { ReactComponent as LogoEN } from '@svgs/logo-outlined-en.svg';
import { ReactComponent as LogoUA } from '@svgs/logo-outlined-ua.svg';
import { ReactComponent as PasskeyIcon } from '@svgs/passkey.svg';
import { ReactComponent as TiktokIcon } from '@svgs/tiktok-green.svg';
import { ReactComponent as YoutubeIcon } from '@svgs/youtube-green.svg';
import { func, object } from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { useOnlineStatusDetector } from '../../hooks/useOnlineStatusDetector';
import { Icon } from '../Icon';
import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher';

const ModalBurgerContent = ({
  closeModal,
  containerProps,
  logInButtonClick,
}) => {
  const { jwtAuthenticationToken, language } = useSelector(
    (state) => state.authSlice
  );
  const { isOnline } = useOnlineStatusDetector();
  const { navigate } = useNavigateWithOfflineMode();
  const { i18n } = useTranslation();

  const { isTabletLG } = useScreenSize();

  const handleNavigateInstagram = () => {
    closeModal();
    window.open(SOCIALS.INSTAGRAM, '_blank');
  };
  const handleNavigateYoutube = () => {
    closeModal();
    window.open(SOCIALS.YOUTUBE, '_blank');
  };
  const handleNavigateFacebook = () => {
    closeModal();
    window.open(SOCIALS.FACEBOOK, '_blank');
  };
  const handleNavigateTiktok = () => {
    closeModal();
    window.open(SOCIALS.TIKTOK, '_blank');
  };

  const closeModalNavigate = (route) => () => {
    closeModal();
    navigate(route);
  };

  const { data } = userApi.useGetUserQuery(undefined, {
    skip: !jwtAuthenticationToken || !isOnline,
  });
  const downloadedAvatar = useGetDownloadedAvatar();

  const LogoIcon = {
    en: LogoEN,
    ua: LogoUA,
  };

  return (
    <Flex
      alignItems="center"
      flexDirection="column"
      gap={containerProps?.linksGap}
      height="100%"
      justifyContent="space-between"
      p={16}
      pt={32}
    >
      <Flex>
        <Icon height={152} SVG={LogoIcon[language || 'en']} width={114} />
        <Flex
          flexDirection="column"
          flexGrow={0}
          gap={8}
          justifyContent="center"
          ml={28}
          mr={28}
        >
          <Typography
            color={theme.colors.white[60]}
            maxWidth={255}
            variant="MS-regular-s16-lh25"
          >
            {i18n.t('Footer.PlatformDescription')}
          </Typography>
          <Typography
            color={theme.colors.schoolBusYellow[100]}
            variant="MS-regular-s16-lh25"
          >
            <a
              href={SOCIALS.LANDING}
              rel="noreferrer"
              style={{
                color: theme.colors.schoolBusYellow[100],
                textDecoration: 'none',
              }}
              target="_blank"
            >
              {i18n.t('Footer.WebsiteLink')}
            </a>
          </Typography>
        </Flex>
      </Flex>
      <Flex flexDirection="column" gap={24}>
        <Button
          as={Typography}
          color={theme.colors.white[100]}
          cursor="pointer"
          fontSize={18}
          fontWeight={600}
          height={30}
          onClick={closeModalNavigate(routes.courses)}
          variant="textButton"
        >
          {i18n.t('Footer.Courses').toUpperCase()}
        </Button>
        <Button
          as={Typography}
          color={theme.colors.white[100]}
          cursor="pointer"
          fontSize={18}
          fontWeight={600}
          height={30}
          onClick={
            jwtAuthenticationToken
              ? closeModalNavigate(routes.learningMap)
              : logInButtonClick
          }
          variant="textButton"
        >
          {i18n.t('Footer.LearningMap').toUpperCase()}
        </Button>
        <Button
          as={Typography}
          color={theme.colors.white[100]}
          cursor="pointer"
          fontSize={18}
          fontWeight={600}
          height={30}
          onClick={closeModalNavigate(routes.activities)}
          variant="textButton"
        >
          {i18n.t('Footer.Activities').toUpperCase()}
        </Button>
        <Button
          as={Typography}
          color={theme.colors.white[100]}
          cursor="pointer"
          fontSize={18}
          fontWeight={600}
          height={30}
          onClick={closeModalNavigate(routes.about)}
          variant="textButton"
        >
          {i18n.t('Footer.AboutUs').toUpperCase()}
        </Button>
      </Flex>
      {isTabletLG && isOnline && <LanguageSwitcher variant="contained" />}
      <Flex flexDirection="column" gap={containerProps?.linksGap} width="100%">
        {jwtAuthenticationToken ? (
          <Button onClick={() => navigate(routes.profile)} variant="textButton">
            <Image
              alt="Profile picture"
              borderRadius={28}
              height={446}
              maxHeight={44}
              minHeight={44}
              minWidth={44}
              src={isOnline ? data?.data.avatar : downloadedAvatar}
              width={44}
            />
          </Button>
        ) : (
          <Button
            minHeight={68}
            onClick={logInButtonClick}
            px={24}
            variant="primary"
            whiteSpace="nowrap"
            width="100%"
          >
            <Icon color={theme.colors.black[100]} mr={1} SVG={PasskeyIcon} />
            <Typography
              color={theme.colors.black[100]}
              variant="MS-semibold-s14-lh15"
            >
              {i18n.t('Footer.Login')?.toUpperCase()}
            </Typography>
          </Button>
        )}
        <Flex
          alignItems="center"
          flexDirection="column"
          height="100%"
          pl={containerProps?.pl}
          pr={containerProps?.pr}
        >
          <Flex gap={8}>
            <Button
              borderRadius={29}
              height={containerProps?.socialButtonSize}
              onClick={handleNavigateInstagram}
              p={0}
              variant="primary"
              width={containerProps?.socialButtonSize}
            >
              <Icon height={30} SVG={InstagramIcon} width={30} />
            </Button>
            <Button
              borderRadius={29}
              height={containerProps?.socialButtonSize}
              onClick={handleNavigateYoutube}
              p={0}
              variant="primary"
              width={containerProps?.socialButtonSize}
            >
              <Icon height={30} SVG={YoutubeIcon} width={30} />
            </Button>
            <Button
              borderRadius={29}
              height={containerProps?.socialButtonSize}
              onClick={handleNavigateFacebook}
              p={0}
              variant="primary"
              width={containerProps?.socialButtonSize}
            >
              <Icon height={30} SVG={FacebookIcon} width={30} />
            </Button>
            <Button
              borderRadius={29}
              height={containerProps?.socialButtonSize}
              onClick={handleNavigateTiktok}
              p={0}
              variant="primary"
              width={containerProps?.socialButtonSize}
            >
              <Icon height={30} SVG={TiktokIcon} width={30} />
            </Button>
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
};

ModalBurgerContent.propTypes = {
  closeModal: func.isRequired,
  containerProps: object,
  logInButtonClick: func.isRequired,
};

ModalBurgerContent.defaultProps = {
  containerProps: {},
};

export default ModalBurgerContent;
